import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppModal } from '../components/AppModal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { StaffPageHeader } from '../components/StaffPageHeader';
import { JournalText } from 'react-bootstrap-icons';
import { CategoryForm } from '../components/Guides/CategoryForm';
import { GuideForm } from '../components/Guides/GuideForm';
import { useAccount } from '../Context/AccountProvider';
import {
  getStaffCategories,
  reorderCategories,
  reorderGuides,
} from '../services/GuideService';
import './StaffGuideManagement.css';

// ── Sortable category wrapper ─────────────────────────────────────────────────

function SortableCategoryCard({ category, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.Id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sgm-category-card${isDragging ? ' sgm-category-card--dragging' : ''}`}
    >
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

// ── Sortable guide row ────────────────────────────────────────────────────────

function SortableGuideRow({ guide, onEdit, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: guide.Id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className="sgm-guide-row">
      <span className="sgm-drag-handle" {...attributes} {...listeners} title="Drag to reorder">⠿</span>
      {children}
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  return <span className={`sgm-badge sgm-badge--${status}`}>{status}</span>;
}

// ── Guide list for one category ───────────────────────────────────────────────

function CategoryGuideList({ categoryId, guides, onEdit, onReordered, toast }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = guides.findIndex(g => g.Id === active.id);
    const newIndex = guides.findIndex(g => g.Id === over.id);
    const reordered = arrayMove(guides, oldIndex, newIndex);

    onReordered(categoryId, reordered);

    reorderGuides(reordered.map(g => g.Id)).catch(() => {
      toast('error', 'Reorder Failed', 'Could not save guide order. Please try again.');
      onReordered(categoryId, guides); // revert
    });
  }

  if (guides.length === 0) {
    return <p className="sgm-guide-empty">No guides yet.</p>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={guides.map(g => g.Id)} strategy={verticalListSortingStrategy}>
        {guides.map(g => (
          <SortableGuideRow key={g.Id} guide={g}>
            <span className="sgm-guide-title">{g.Title}</span>
            <span className="sgm-guide-meta">
              <StatusBadge status={g.Status} />
              {g.PublishAt && (
                <span title="Publish at">{new Date(g.PublishAt).toLocaleDateString()}</span>
              )}
            </span>
            <button className="sgm-btn" onClick={() => onEdit(g.Id)}>Edit</button>
          </SortableGuideRow>
        ))}
      </SortableContext>
    </DndContext>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function StaffGuideManagement() {
  const { toast } = useAccount();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  // Modal state
  const [catModal, setCatModal] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [guideModal, setGuideModal] = useState(false);
  const [editGuideId, setEditGuideId] = useState(null);
  const [newGuideCategoryId, setNewGuideCategoryId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getStaffCategories();
        if (!cancelled) {
          setCategories(data);
          // Expand all categories by default
          setExpanded(new Set(data.map(c => c.Id)));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Category drag-and-drop ────────────────────────────────────────────────

  function handleCategoryDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex(c => c.Id === active.id);
    const newIndex = categories.findIndex(c => c.Id === over.id);
    const reordered = arrayMove(categories, oldIndex, newIndex);

    setCategories(reordered);

    reorderCategories(reordered.map(c => c.Id)).catch(() => {
      toast('error', 'Reorder Failed', 'Could not save category order. Please try again.');
      setCategories(categories);
    });
  }

  // ── Guide reorder callback ────────────────────────────────────────────────

  function handleGuideReordered(categoryId, reorderedGuides) {
    setCategories(prev => prev.map(c =>
      c.Id === categoryId ? { ...c, Guides: reorderedGuides } : c
    ));
  }

  // ── Category modal ────────────────────────────────────────────────────────

  function openNewCategory() {
    setEditCategory(null);
    setCatModal(true);
  }

  function openEditCategory(cat, e) {
    e.stopPropagation();
    setEditCategory(cat);
    setCatModal(true);
  }

  function handleCategorySaved(savedCat) {
    setCategories(prev => {
      const existing = prev.find(c => c.Id === savedCat.Id);
      if (existing) return prev.map(c => c.Id === savedCat.Id ? { ...c, ...savedCat } : c);
      return [...prev, savedCat];
    });
    setCatModal(false);
    toast('success', editCategory ? 'Category Updated' : 'Category Created',
      editCategory ? 'Category saved.' : 'New category created.');
  }

  // ── Guide modal ───────────────────────────────────────────────────────────

  function openNewGuide(catId, e) {
    e.stopPropagation();
    setEditGuideId(null);
    setNewGuideCategoryId(catId);
    setGuideModal(true);
  }

  function openEditGuide(guideId) {
    setEditGuideId(guideId);
    setNewGuideCategoryId(null);
    setGuideModal(true);
  }

  function handleGuideSaved(savedGuide) {
    setCategories(prev => prev.map(cat => {
      if (cat.Id !== savedGuide.CategoryId) return cat;
      const existing = (cat.Guides ?? []).find(g => g.Id === savedGuide.Id);
      if (existing) {
        return { ...cat, Guides: cat.Guides.map(g => g.Id === savedGuide.Id ? { ...g, ...savedGuide } : g) };
      }
      return { ...cat, Guides: [...(cat.Guides ?? []), savedGuide] };
    }));
    setGuideModal(false);
    toast('success', editGuideId ? 'Guide Updated' : 'Guide Created',
      editGuideId ? 'Guide saved.' : 'New guide created.');
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="sgm-load-error">Failed to load guide data.</p>;

  function toggleExpand(catId) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      return next;
    });
  }

  return (
    <div className="sgm-page">
      <StaffPageHeader
        icon={<JournalText />}
        title="Guide Editor"
        actions={
          <Button className="sgm-btn sgm-btn--primary" onClick={openNewCategory}>
            New Category
          </Button>
        }
      />

      {categories.length === 0 ? (
        <EmptyState message="No categories yet." ctaLabel="New Category" />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
          <SortableContext items={categories.map(c => c.Id)} strategy={verticalListSortingStrategy}>
            <div className="sgm-category-list">
              {categories.map(cat => (
                <SortableCategoryCard key={cat.Id} category={cat}>
                  {({ dragHandleProps }) => (
                    <>
                      <div className="sgm-category-header" onClick={() => toggleExpand(cat.Id)}>
                        <span className="sgm-drag-handle" {...dragHandleProps} onClick={e => e.stopPropagation()} title="Drag to reorder">⠿</span>
                        <span className="sgm-cat-name">{cat.Name}</span>
                        <span className="sgm-cat-meta">
                          <span className={`sgm-badge sgm-badge--${cat.IsEnabled ? 'enabled' : 'disabled'}`}>
                            {cat.IsEnabled ? 'enabled' : 'disabled'}
                          </span>
                          <span>{cat.Guides?.length ?? 0} guides</span>
                        </span>
                        <div className="sgm-cat-actions" onClick={e => e.stopPropagation()}>
                          <button className="sgm-btn" onClick={e => openEditCategory(cat, e)}>Edit</button>
                          <button className="sgm-btn sgm-btn--primary" onClick={e => openNewGuide(cat.Id, e)}>+ Guide</button>
                        </div>
                        <span className={`sgm-expand-chevron${expanded.has(cat.Id) ? ' sgm-expand-chevron--open' : ''}`}>▼</span>
                      </div>

                      {expanded.has(cat.Id) && (
                        <div className="sgm-guide-list">
                          <CategoryGuideList
                            categoryId={cat.Id}
                            guides={cat.Guides ?? []}
                            onEdit={openEditGuide}
                            onReordered={handleGuideReordered}
                            toast={toast}
                          />
                        </div>
                      )}
                    </>
                  )}
                </SortableCategoryCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Category modal */}
      <AppModal show={catModal} onHide={() => setCatModal(false)} size="md">
        <AppModal.Header closeButton>
          <AppModal.Title>{editCategory ? 'Edit Category' : 'New Category'}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          <CategoryForm category={editCategory} onSaved={handleCategorySaved} />
        </AppModal.Body>
      </AppModal>

      {/* Guide modal */}
      <AppModal show={guideModal} onHide={() => setGuideModal(false)} size="xl">
        <AppModal.Header closeButton>
          <AppModal.Title>{editGuideId ? 'Edit Guide' : 'New Guide'}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          <GuideForm
            guideId={editGuideId}
            defaultCategoryId={newGuideCategoryId}
            categories={categories}
            onSaved={handleGuideSaved}
          />
        </AppModal.Body>
      </AppModal>
    </div>
  );
}
